import { Layout } from 'components/Layout';
import { MetaHead } from 'components/MetaHead';
import { IndustryData, IndustrySubTopic } from 'constants/industries/industries';
import { FC, useState } from 'react';
import {
  IndustryAccess,
  IndustryAccessHiddenBlock,
  IndustryAccessHiddenBlockItem,
  IndustryAccessHiddenBlockItemText,
  IndustryAccessHiddenBlockItemTitle,
  IndustryAccessIcons,
  IndustryAccessLink,
  IndustryAccessMoreButton,
  IndustryAccessText,
  IndustryAccessTextWrapper,
  IndustryAccessTitle,
  IndustryAccessTitleWrapper,
  IndustryAccessWrapper,
  IndustryHeroOverlay,
  IndustryHeroWrapper,
  IndustryMain,
  IndustryMainButtonWrapper,
  IndustryMainDesc,
  IndustryMainImage,
  IndustryMainTitle,
  IndustryMainVideo,
  IndustryMainWrapper,
  IndustryTopic,
  IndustryTopicAlt,
  IndustryTopicImageWrapper,
  IndustryTopicList,
  IndustryTopicListAlt,
  IndustryTopicListItem,
  IndustryTopicListItemAlt,
  IndustryTopicListItemAltTitle,
  IndustryTopicSubTitle,
  IndustryTopicTextWrapper,
  IndustryTopicTitle,
  IndustryTopicWrapper,
  IndustryUseCases,
  IndustryUseCasesItem,
  IndustryUseCasesSubTitle,
  IndustryUseCasesTitle,
  IndustryUseCasesTop,
  IndustryUseCasesWrapper,
} from './IndustryTemplate.style';
import { AnimatePresence, motion } from 'framer-motion';
import { LeftTriangle, RightTriangle } from 'components/HeroSection/HeroSection.styles';
import { ContactModal } from 'components/Header/ContactModal';
import { ReadMore } from 'components/ReadMore/ReadMore';
import chevron from 'assets/common/icons/chevron-white.svg';
import { Link } from 'react-router-dom';
import { Button } from 'components/Buttons';
import TextWithImage from 'modules/Application/TextWithImage/TextWithImage';
import { useIsMobile } from 'hooks/useIsMobile';

export const IndustryTemplate: FC<IndustryData> = ({
  meta,
  additionalDescription,
  topContent,
  topics,
  access,
  useCases,
  castomerCasesData,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useIsMobile();

  const toggleModal = () => setIsModalOpen((value) => !value);

  return (
    <Layout>
      <MetaHead pageMeta={meta} />

      <IndustryHeroWrapper>
        {topContent.type === 'video' ? (
          <IndustryMainVideo
            src={topContent.src}
            autoPlay
            muted
            loop
            playsInline
          >
            No video
          </IndustryMainVideo>
        ) : (
          <IndustryMainImage src={topContent.src} />
        )}
        <IndustryHeroOverlay />

        <IndustryMainWrapper>
          <IndustryMain>
            <IndustryMainTitle>{meta.title}</IndustryMainTitle>
            <IndustryMainDesc>
              <p>{meta.description}</p>
              {additionalDescription && <p>{additionalDescription}</p>}
              <IndustryMainButtonWrapper>
                <Button onClick={toggleModal} width={isMobile ? 'full' : 135} px={20}>
                  Contact us
                </Button>
                <Link to={topContent.path}>
                  <Button btnType="secondary" width={218} px={20}>
                    Explore Platform
                  </Button>
                </Link>
              </IndustryMainButtonWrapper>
            </IndustryMainDesc>
          </IndustryMain>
          <LeftTriangle />
          <RightTriangle />
        </IndustryMainWrapper>
      </IndustryHeroWrapper>

      <IndustryTopicWrapper>
        {topics.map((topic, topicIndex) => (
          <>
            {topicIndex === 0 ? (
              <IndustryTopic key={topic.title}>
                <IndustryTopicTextWrapper>
                  <IndustryTopicTitle>{topic.title}</IndustryTopicTitle>
                  <IndustryTopicSubTitle>{topic.text}</IndustryTopicSubTitle>
                  {typeof topic.list[0] === 'string' ? (
                    <IndustryTopicList>
                      {(topic.list as string[]).map((item, index) => (
                        <IndustryTopicListItem key={index}>{item}</IndustryTopicListItem>
                      ))}
                    </IndustryTopicList>
                  ) : (
                    <IndustryTopicListAlt>
                      {(topic.list as IndustrySubTopic[]).map((item, index) => (
                        <IndustryTopicListItemAlt key={index}>
                          <IndustryTopicListItemAltTitle>{item.title}</IndustryTopicListItemAltTitle>
                          <span>{item.text}</span>
                        </IndustryTopicListItemAlt>
                      ))}
                    </IndustryTopicListAlt>
                  )}
                </IndustryTopicTextWrapper>
                <IndustryTopicImageWrapper>
                  <img src={topic.image} alt="Simulation" />
                </IndustryTopicImageWrapper>
              </IndustryTopic>
            ) : (
              <IndustryTopicAlt key={topic.title}>
                <IndustryTopicTextWrapper>
                  <IndustryTopicTitle>{topic.title}</IndustryTopicTitle>
                  <IndustryTopicSubTitle>{topic.text}</IndustryTopicSubTitle>
                  {typeof topic.list[0] === 'string' ? (
                    <IndustryTopicList>
                      {(topic.list as string[]).map((item, index) => (
                        <IndustryTopicListItem key={index}>{item}</IndustryTopicListItem>
                      ))}
                    </IndustryTopicList>
                  ) : (
                    <IndustryTopicListAlt>
                      {(topic.list as IndustrySubTopic[]).map((item, index) => (
                        <IndustryTopicListItemAlt key={index}>
                          <IndustryTopicListItemAltTitle>{item.title}</IndustryTopicListItemAltTitle>
                          <span>{item.text}</span>
                        </IndustryTopicListItemAlt>
                      ))}
                    </IndustryTopicListAlt>
                  )}
                </IndustryTopicTextWrapper>
                <IndustryTopicImageWrapper>
                  <img src={topic.image} alt="Simulation" />
                </IndustryTopicImageWrapper>
              </IndustryTopicAlt>
            )}
            {topicIndex === 0 && (
              <>
                <IndustryUseCasesTop>
                  <IndustryUseCasesTitle>{useCases.title}</IndustryUseCasesTitle>
                  <IndustryUseCasesSubTitle>{useCases.text}</IndustryUseCasesSubTitle>
                </IndustryUseCasesTop>
                <IndustryUseCases itemQuantity={useCases.cases.length}>
                  {useCases.cases.map((useCase) => (
                    <IndustryUseCasesItem key={useCase.title}>
                      <img src={useCase.image} alt="Use case image" />
                      <h5>{useCase.title}</h5>
                      <ReadMore text={useCase.text} lineHeight={24} />
                    </IndustryUseCasesItem>
                  ))}
                </IndustryUseCases>
              </>
            )}
          </>
        ))}
      </IndustryTopicWrapper>

      <IndustryAccessWrapper>
        <IndustryAccess>
          <IndustryAccessTitleWrapper>
            <IndustryAccessTitle>{access.title}</IndustryAccessTitle>
            {access.link && (
              <Link to={access.link.path}>
                <IndustryAccessLink>{access.link.text}</IndustryAccessLink>
              </Link>
            )}
          </IndustryAccessTitleWrapper>
          <IndustryAccessTextWrapper>
            {access.text}
            {access.additionalText && (
              <IndustryAccessText>
                <strong>{access.additionalText.title}</strong> {access.additionalText.text}
              </IndustryAccessText>
            )}
            {access.hiddenBlock && (
              <IndustryAccessMoreButton
                onClick={() => setIsExpanded((prev) => !prev)}
                isExpanded={isExpanded}
              >
                <span>Read more</span>
                <img src={chevron} alt="Chevron icon" />
              </IndustryAccessMoreButton>
            )}
          </IndustryAccessTextWrapper>
          {access.hiddenBlock && (
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  key="hiddenBlock"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <IndustryAccessHiddenBlock>
                    {access.hiddenBlock.map((item) => (
                      <IndustryAccessHiddenBlockItem key={item.title}>
                        <IndustryAccessHiddenBlockItemTitle>
                          {item.title}
                        </IndustryAccessHiddenBlockItemTitle>
                        <IndustryAccessHiddenBlockItemText>
                          {item.text}
                        </IndustryAccessHiddenBlockItemText>
                      </IndustryAccessHiddenBlockItem>
                    ))}
                  </IndustryAccessHiddenBlock>
                </motion.div>
              )}
            </AnimatePresence>
          )}
          <IndustryAccessIcons>
            {access.icons.map((icon) => (
              <img key={icon} src={icon} alt="Access icon" />
            ))}
          </IndustryAccessIcons>
        </IndustryAccess>
        <LeftTriangle />
        <RightTriangle />
      </IndustryAccessWrapper>

      <IndustryUseCasesWrapper>
        {castomerCasesData && <TextWithImage reversed type="customer-case" {...castomerCasesData} />}
      </IndustryUseCasesWrapper>

      <ContactModal onClose={toggleModal} isOpen={isModalOpen} />
    </Layout>
  );
};
